import { THEMES, type Preferences } from '@keep/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useAppConfig,
  useChangePassword,
  useCurrentUser,
  useDeleteAccount,
  useUpdateProfile,
} from '../../api/auth';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { errorMessage } from '../../lib/api';
import { paths } from '../../lib/routes';
import { toast } from '../../stores/toasts';
import { useUi } from '../../stores/ui';

const THEME_LABELS: Record<Preferences['theme'], string> = {
  system: 'Paramètre système',
  light: 'Clair',
  dark: 'Sombre',
};

const inputClass =
  'h-10 w-full rounded border border-line bg-transparent px-3 text-sm outline-none focus:border-primary';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-line px-6 py-4">
      <h3 className="text-sm font-medium">{title}</h3>
      {children}
    </section>
  );
}

function PasswordForm() {
  const change = useChangePassword();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        change.mutate(
          { currentPassword, newPassword },
          {
            onSuccess: () => {
              setCurrent('');
              setNew('');
              toast('Mot de passe modifié. Vos autres sessions ont été déconnectées.');
            },
          },
        );
      }}
    >
      <input
        type="password"
        autoComplete="current-password"
        placeholder="Mot de passe actuel"
        value={currentPassword}
        onChange={(e) => setCurrent(e.target.value)}
        className={inputClass}
        required
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder="Nouveau mot de passe (8 caractères min.)"
        value={newPassword}
        onChange={(e) => setNew(e.target.value)}
        className={inputClass}
        minLength={8}
        required
      />
      {change.error && <p className="text-xs text-danger">{errorMessage(change.error)}</p>}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={change.isPending}>
          Modifier
        </Button>
      </div>
    </form>
  );
}

function DeleteAccount() {
  const remove = useDeleteAccount();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  if (!confirming) {
    return (
      <div>
        <Button variant="danger" className="px-3" onClick={() => setConfirming(true)}>
          Supprimer mon compte…
        </Button>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        remove.mutate(password, { onSuccess: () => navigate(paths.login, { replace: true }) });
      }}
    >
      <p className="text-sm text-muted">
        Toutes vos notes, libellés et images seront définitivement supprimés. Saisissez votre mot de
        passe pour confirmer.
      </p>
      <input
        type="password"
        autoComplete="current-password"
        placeholder="Mot de passe"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className={inputClass}
        required
      />
      {remove.error && <p className="text-xs text-danger">{errorMessage(remove.error)}</p>}
      <div className="flex justify-end gap-2">
        <Button onClick={() => setConfirming(false)}>Annuler</Button>
        <Button type="submit" variant="danger" disabled={remove.isPending}>
          Supprimer définitivement
        </Button>
      </div>
    </form>
  );
}

export function SettingsDialog() {
  const open = useUi((s) => s.settingsOpen);
  const setOpen = useUi((s) => s.setSettingsOpen);
  const setImportOpen = useUi((s) => s.setImportOpen);
  const user = useCurrentUser();
  const update = useUpdateProfile();
  const { data: config } = useAppConfig();
  const [name, setName] = useState(user.name);
  const prefs = user.preferences;

  return (
    <Dialog open={open} onOpenChange={setOpen} title="Paramètres" className="max-w-[520px]">
      <div className="scrollbar-thin overflow-y-auto">
        <Section title="Paramètres des notes">
          <label className="flex cursor-pointer items-center justify-between gap-4 text-sm">
            Déplacer les éléments cochés en bas
            <input
              type="checkbox"
              checked={prefs.moveCheckedToBottom}
              onChange={(e) =>
                update.mutate({ preferences: { moveCheckedToBottom: e.target.checked } })
              }
              className="size-4 accent-[var(--primary)]"
            />
          </label>
          <label className="flex items-center justify-between gap-4 text-sm">
            Thème
            <select
              value={prefs.theme}
              onChange={(e) =>
                update.mutate({ preferences: { theme: e.target.value as Preferences['theme'] } })
              }
              className="h-9 rounded border border-line bg-bg px-2 text-sm"
            >
              {THEMES.map((theme) => (
                <option key={theme} value={theme}>
                  {THEME_LABELS[theme]}
                </option>
              ))}
            </select>
          </label>
        </Section>
        <Section title="Importer">
          <div className="flex items-center justify-between gap-4 text-sm">
            <span>Récupérez vos notes, listes, libellés et images depuis Google Keep.</span>
            <Button
              className="shrink-0 border border-line"
              onClick={() => {
                setOpen(false);
                setImportOpen(true);
              }}
            >
              Importer…
            </Button>
          </div>
        </Section>
        <Section title="Profil">
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() && name.trim() !== user.name) {
                update.mutate({ name: name.trim() }, { onSuccess: () => toast('Nom mis à jour') });
              }
            }}
          >
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className={inputClass}
              aria-label="Nom"
            />
            <Button type="submit">Enregistrer</Button>
          </form>
          <p className="text-xs text-muted">{user.email}</p>
        </Section>
        <Section title="Mot de passe">
          <PasswordForm />
        </Section>
        <Section title="Compte">
          <DeleteAccount />
        </Section>
        <p className="border-t border-line px-6 py-3 text-xs text-muted">
          Raccourcis : <kbd>c</kbd> nouvelle note · <kbd>l</kbd> nouvelle liste · <kbd>/</kbd>{' '}
          rechercher · <kbd>Échap</kbd> fermer · <kbd>Ctrl</kbd>+<kbd>Entrée</kbd> enregistrer et
          fermer
          {config?.version && <span className="block pt-1">Version {config.version}</span>}
        </p>
      </div>
      <div className="flex justify-end border-t border-line px-4 py-2">
        <Button onClick={() => setOpen(false)}>Fermer</Button>
      </div>
    </Dialog>
  );
}
